<template>
    <section class="w-full flex flex-col pt-16 justify-center pb-10 px-5 max-w-6xl mx-auto">

        <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
                <h1 class="text-2xl font-bold text-secondary">Categorias de Cursos</h1>
                <p class="text-sm text-gray-500">Gerencie as taxonomias e traduções globais do sistema.</p>
            </div>
            <Button label="Nova Categoria" icon="pi pi-plus" class="bg-button shadow" @click="openCreateModal" />
        </div>

        <div class="w-full bg-white rounded-xl shadow-md border border-iron overflow-hidden">
            <DataTable :value="categories" :loading="state.loading" dataKey="id" responsiveLayout="scroll"
                class="p-datatable-sm" :rows="10" paginator
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown">
                <template #empty>
                    <div class="text-center py-8 text-gray-400 italic">
                        Nenhuma categoria encontrada.
                    </div>
                </template>

                <Column header="Nome Visualizado (Idioma Atual)" style="width: 30%">
                    <template #body="slotProps">
                        <span class="font-semibold text-secondary">
                            {{ slotProps.data.getTranslatedName() }}
                        </span>
                    </template>
                </Column>

                <Column header="Traduções Disponíveis" style="width: 45%">
                    <template #body="slotProps">
                        <div class="flex flex-col gap-1 text-xs text-gray-600">
                            <div class="flex items-center gap-2">
                                <img :src="getOnPublic('/flags/br.svg')" class="w-3 h-2 object-cover" alt="PT" />
                                <span class="font-medium text-gray-800">{{ slotProps.data.pt || '---' }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <img :src="getOnPublic('/flags/en.svg')" class="w-3 h-2 object-cover" alt="EN" />
                                <span class="text-gray-500">{{ slotProps.data.en || '---' }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <img :src="getOnPublic('/flags/es.svg')" class="w-3 h-2 object-cover" alt="ES" />
                                <span class="text-gray-500">{{ slotProps.data.es || '---' }}</span>
                            </div>
                        </div>
                    </template>
                </Column>

                <Column field="name" header="Slug / System Name" style="width: 15%">
                    <template #body="slotProps">
                        <Tag severity="secondary" value="info"
                            class="font-mono text-xs bg-gray-100 text-gray-600 border border-gray-200">
                            {{ slotProps.data.name }}
                        </Tag>
                    </template>
                </Column>

                <Column header="Ações" style="width: 10%" class="text-center">
                    <template #body="slotProps">
                        <div class="flex gap-2 justify-center">
                            <Button icon="pi pi-pencil" severity="secondary" variant="text" rounded
                                v-tooltip.top="'Editar'" @click="openEditModal(slotProps.data)" />
                            <Button icon="pi pi-trash" severity="danger" variant="text" rounded
                                v-tooltip.top="'Excluir'" @click="confirmDelete(slotProps.data)" />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>

        <CategoryFormDialog v-model="state.isModalOpen" :category="state.selectedCategory" :loading="state.modalLoading"
            @save="handleSaveCategory" />
    </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { showToast } from '@/composables/toast';
import { getOnPublic } from '@/composables/imageChecker';
import CategoryFormDialog from './CategoryFormDialog.vue';

import Category from '@/api/models/Category';
import CategoryService from '@/services/CategoryService';
import { translate } from '@/composables/translate';

const toast = useToast();
const categories = ref([]);

const state = reactive({
    loading: false,
    modalLoading: false,
    isModalOpen: false,
    selectedCategory: null
});

onMounted(() => {
    fetchCategories();
});

async function fetchCategories() {
    state.loading = true;
    try {
        categories.value = await CategoryService.all();

    } catch (error) {
        showToast(toast, translate('no category found', 'ucfirst'), 'error');

    } finally {
        state.loading = false;
    }
}

const openCreateModal = () => {
    state.selectedCategory = new Category({});
    state.isModalOpen = true;
};

const openEditModal = (category) => {
    state.selectedCategory = category;
    state.isModalOpen = true;
};

async function handleSaveCategory(formData) {
    state.modalLoading = true;
    try {
        const response = await CategoryService.save(formData, formData.id);

        if (response.success()) {
            showToast(toast, formData.id ? 'Categoria atualizada!' : 'Categoria criada!', 'success');
            state.isModalOpen = false;
            fetchCategories();

        } else {
            showToast(toast, 'Verifique os dados informados.', 'error');
        }

    } catch (error) {
        showToast(toast, 'Erro operacional ao salvar.', 'error');
    } finally {
        state.modalLoading = false;
    }
}

async function confirmDelete(category) {
    if (confirm(`Deseja realmente remover a categoria "${category.getTranslatedName()}"?`)) {
        state.loading = true;
        try {
            const response = await CategoryService.remove(category.id);
            if (response.success()) {
                showToast(toast, 'Categoria removida com sucesso.', 'success');
                fetchCategories();
            }
        } catch (e) {
            showToast(toast, 'Não foi possível excluir a categoria.', 'error');
        } finally {
            state.loading = false;
        }
    }
}
</script>